import { useEffect, useMemo, useState } from "react";
import {
  Check, CheckCheck, CheckSquare, Inbox, Loader2, Mail, MailOpen,
  Search as SearchIcon, Square, Trash2, X,
} from "lucide-react";

import { useConfirm } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useBulkDeleteContactMessages, useBulkUpdateContactMessages,
  useContactMessages, useDeleteContactMessage,
  useMarkAllContactMessagesRead, useUnreadContactMessageCount,
  useUpdateContactMessage,
  type ContactMessageRow,
} from "@/hooks/useContactMessages";
import { cn } from "@/lib/utils";

/**
 * /contact-inbox — Director+ surface. Visitor-submitted messages from
 * the public /contact/ form, triaged inbox-style.
 *
 * Layout: list on the left, detail on the right (desktop). On mobile
 * the detail panel slides in as a full-width view that hides the list
 * until you "Back" out.
 *
 * Backed by /api/v1/contact-messages/ which is locked to Director+ —
 * regular users can't reach this surface (Sidebar hides the link too).
 */
export default function ContactInbox() {
  const [filter, setFilter] = useState<"all" | "unread" | "read">("all");
  const [query, setQuery]   = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Server query — keeps it simple: filter the unread param server-side,
  // search server-side too (DRF SearchFilter scans name+email+subject+message).
  const listOpts = useMemo(() => {
    const o: { unread?: boolean | null; search?: string } = {};
    if (filter === "unread") o.unread = true;
    if (filter === "read")   o.unread = false;
    if (query.trim())        o.search = query.trim();
    return o;
  }, [filter, query]);

  const list       = useContactMessages(listOpts);
  const update     = useUpdateContactMessage();
  const del        = useDeleteContactMessage();
  const markAll    = useMarkAllContactMessagesRead();
  const bulkUpdate = useBulkUpdateContactMessages();
  const bulkDelete = useBulkDeleteContactMessages();
  const confirm    = useConfirm();

  // Global unread count (independent of filter / search) — used for the
  // big red badge next to the page title so it's always accurate.
  const unreadTotal = useUnreadContactMessageCount();
  const totalUnread = unreadTotal.data?.unread ?? 0;

  // Multi-select
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const bulkBusy = bulkUpdate.isPending || bulkDelete.isPending || markAll.isPending;
  function toggleSelect(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function clearSelection() { setSelected(new Set()); }

  const rows = list.data ?? [];
  const visibleIds = useMemo(() => new Set(rows.map((r) => r.id)), [rows]);
  const allVisibleSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  function selectAllVisible() {
    setSelected(new Set([...selected, ...visibleIds]));
  }

  // Trim selection when rows disappear (delete / filter change)
  useEffect(() => {
    if (selected.size === 0) return;
    const existing = new Set(rows.map((r) => r.id));
    const trimmed = new Set([...selected].filter((id) => existing.has(id)));
    if (trimmed.size !== selected.size) setSelected(trimmed);
  }, [rows, selected]);

  // Esc clears selection (or the detail view, if nothing selected)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (selected.size > 0 && !bulkBusy) {
        clearSelection();
      } else if (selectedId !== null) {
        setSelectedId(null);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected.size, bulkBusy, selectedId]);

  // Auto-mark the opened message as read
  const openMsg = useMemo(() => rows.find((r) => r.id === selectedId) ?? null, [rows, selectedId]);
  useEffect(() => {
    if (openMsg && !openMsg.is_read && !update.isPending) {
      update.mutate({ id: openMsg.id, is_read: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMsg?.id]);

  const stats = useMemo(() => {
    // Note: `rows` is already filtered by the server. For the stats
    // header we'd ideally hit a separate count endpoint, but the list
    // is small and the inbox view typically loads /all/ first.
    return {
      total:  rows.length,
      unread: rows.filter((r) => !r.is_read).length,
    };
  }, [rows]);

  async function onDelete(row: ContactMessageRow) {
    const ok = await confirm({
      title: "Delete this message?",
      description: `Message from ${row.name || row.email} ("${row.subject.slice(0, 60)}${row.subject.length > 60 ? "…" : ""}") will be removed permanently.`,
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    if (selectedId === row.id) setSelectedId(null);
    del.mutate(row.id);
  }

  async function onBulkDelete() {
    if (selected.size === 0) return;
    const ok = await confirm({
      title: `Delete ${selected.size} message${selected.size === 1 ? "" : "s"}?`,
      description: "Selected messages will be removed permanently.",
      confirmLabel: `Delete ${selected.size}`,
      destructive: true,
    });
    if (!ok) return;
    await bulkDelete.mutateAsync([...selected]);
    clearSelection();
    if (selectedId !== null && selected.has(selectedId)) setSelectedId(null);
  }
  async function onBulkRead(is_read: boolean) {
    if (selected.size === 0) return;
    await bulkUpdate.mutateAsync({ ids: [...selected], is_read });
    clearSelection();
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {/* ---------- Header ---------- */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
            <Inbox className="h-3.5 w-3.5" />Public contact inbox
          </div>
          {/* Title + prominent "N new" counter so the at-a-glance backlog
              is obvious without having to scan the stats strip. Shown
              only when there is something new, fed by the dedicated
              unread-count endpoint (not filtered by search/chip). */}
          <div className="mt-1 flex items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight">Contact messages</h1>
            {totalUnread > 0 && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-rose-500 px-3 py-1 text-sm font-bold text-white shadow-soft animate-in fade-in slide-in-from-left-1"
                aria-label={`${totalUnread} unread messages`}
                title={`${totalUnread} unread`}
              >
                <span className="tabular-nums">{totalUnread}</span>
                <span className="text-xs font-medium opacity-90">new</span>
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Messages submitted via the public{" "}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">/contact/</code>{" "}
            form. Visible to directors and above.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => markAll.mutate()}
          disabled={markAll.isPending || totalUnread === 0}
          className="shrink-0"
        >
          {markAll.isPending
            ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Marking…</>
            : <><CheckCheck className="mr-2 h-4 w-4" />Mark all as read</>}
        </Button>
      </header>

      {/* ---------- Stat strip + filter + search ---------- */}
      <div className="flex flex-wrap items-center gap-2">
        <StatPill label="In view" value={stats.total} />
        <StatPill label="Unread" value={stats.unread} accent="text-rose-600" />
        <div className="flex items-center gap-1 rounded-md border border-border/60 bg-card p-1">
          <FilterChip current={filter} value="all"    onClick={setFilter}>All</FilterChip>
          <FilterChip current={filter} value="unread" onClick={setFilter}>Unread</FilterChip>
          <FilterChip current={filter} value="read"   onClick={setFilter}>Read</FilterChip>
        </div>
        <div className="relative ml-auto w-full max-w-xs">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, subject…"
            className="pl-9"
          />
        </div>
      </div>

      {/* ---------- Bulk action bar ---------- */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-30 -mx-2 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 shadow-soft backdrop-blur supports-[backdrop-filter]:bg-primary/10">
          <div className="flex items-center gap-2 text-sm font-medium text-primary">
            <CheckSquare className="h-4 w-4" />
            {selected.size} selected
          </div>
          <Button variant="ghost" size="sm" onClick={selectAllVisible} disabled={bulkBusy || allVisibleSelected} className="h-7">
            Select all visible
          </Button>
          <div className="mx-1 hidden h-5 w-px bg-border/60 sm:block" />
          <Button variant="outline" size="sm" onClick={() => onBulkRead(true)}  disabled={bulkBusy} className="h-7">
            <MailOpen className="mr-1.5 h-3.5 w-3.5" />Mark read
          </Button>
          <Button variant="outline" size="sm" onClick={() => onBulkRead(false)} disabled={bulkBusy} className="h-7">
            <Mail className="mr-1.5 h-3.5 w-3.5" />Mark unread
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={onBulkDelete} disabled={bulkBusy}
            className="h-7 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete
          </Button>
          <Button variant="ghost" size="sm" onClick={clearSelection} disabled={bulkBusy} className="ml-auto h-7">
            {bulkBusy
              ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Working…</>
              : <><X className="mr-1.5 h-3.5 w-3.5" />Clear</>}
          </Button>
        </div>
      )}

      {/* ---------- Master / detail ---------- */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        {/* --- List --- */}
        <Card className={cn(
          "border-border/60 shadow-soft",
          // Hide on mobile while a message is open
          selectedId !== null && "hidden lg:block",
        )}>
          <CardContent className="p-0">
            {list.isLoading ? (
              <div className="space-y-2 p-3">
                {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            ) : rows.length === 0 ? (
              <div className="px-6 py-16 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                  <Inbox className="h-5 w-5 text-muted-foreground" />
                </div>
                <h3 className="text-sm font-semibold">
                  {query ? "No messages match your search" : filter === "unread" ? "No unread messages" : "Inbox is empty"}
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  {query
                    ? "Try a different keyword or clear the search."
                    : filter === "unread"
                      ? "All caught up — switch to All to see read ones."
                      : "Messages from the public /contact/ form will appear here."}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-border/60">
                {rows.map((row) => (
                  <MessageRow
                    key={row.id}
                    row={row}
                    active={selectedId === row.id}
                    isSelected={selected.has(row.id)}
                    anySelected={selected.size > 0}
                    onOpen={() => setSelectedId(row.id)}
                    onToggleSelect={() => toggleSelect(row.id)}
                  />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* --- Detail --- */}
        <Card className={cn(
          "border-border/60 shadow-soft",
          // On mobile, show only when a message is open; hide otherwise
          selectedId === null && "hidden lg:block",
        )}>
          <CardContent className="p-0">
            {openMsg ? (
              <MessageDetail
                row={openMsg}
                onClose={() => setSelectedId(null)}
                onToggleRead={() => update.mutate({ id: openMsg.id, is_read: !openMsg.is_read })}
                onDelete={() => onDelete(openMsg)}
                busy={update.isPending || del.isPending}
              />
            ) : (
              <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-2 px-8 py-16 text-center text-muted-foreground">
                <Inbox className="h-10 w-10 opacity-50" />
                <p className="text-sm">Select a message to read it.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------
function StatPill({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card px-2.5 py-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("font-semibold tabular-nums", accent ?? "text-foreground")}>{value}</span>
    </span>
  );
}

function FilterChip<T extends string>({
  current, value, onClick, children,
}: {
  current: T;
  value: T;
  onClick: (v: T) => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={cn(
        "rounded px-2.5 py-1 text-xs font-medium transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
      )}
    >
      {children}
    </button>
  );
}

function MessageRow({
  row, active, isSelected, anySelected, onOpen, onToggleSelect,
}: {
  row: ContactMessageRow;
  active: boolean;
  isSelected: boolean;
  anySelected: boolean;
  onOpen: () => void;
  onToggleSelect: () => void;
}) {
  const initial = (row.name || row.email).trim()[0]?.toUpperCase() ?? "?";
  return (
    <li className={cn(
      "group flex items-start gap-3 px-3 py-3 transition-colors",
      active ? "bg-primary/10" : !row.is_read ? "bg-card hover:bg-muted/40" : "hover:bg-muted/40",
      isSelected && "bg-primary/5",
    )}>
      {/* Select checkbox */}
      <button
        type="button"
        onClick={onToggleSelect}
        aria-label={isSelected ? "Deselect" : "Select"}
        aria-pressed={isSelected}
        className={cn(
          "mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors",
          isSelected
            ? "border-primary bg-primary text-primary-foreground"
            : "border-border/70 bg-card text-muted-foreground hover:border-primary/50",
          anySelected ? "opacity-100" : "opacity-0 group-hover:opacity-100",
        )}
      >
        {isSelected ? <Check className="h-3 w-3" /> : <Square className="h-3 w-3 stroke-[1.6]" />}
      </button>

      {/* Avatar bubble */}
      <div className={cn(
        "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold",
        row.is_read
          ? "bg-muted text-muted-foreground"
          : "bg-primary text-primary-foreground",
      )}>
        {initial}
      </div>

      {/* Body — entire area opens the detail */}
      <button
        type="button"
        onClick={onOpen}
        className="min-w-0 flex-1 text-left"
      >
        <div className="flex items-baseline gap-2">
          <span className={cn(
            "truncate text-sm",
            row.is_read ? "font-medium text-foreground" : "font-semibold text-foreground",
          )}>
            {row.name || row.email}
          </span>
          {!row.is_read && (
            <span className="inline-block h-2 w-2 shrink-0 rounded-full bg-rose-500" aria-label="Unread" />
          )}
          <span className="ml-auto shrink-0 text-[10px] text-muted-foreground tabular-nums">
            {formatRelative(row.created_at)}
          </span>
        </div>
        <div className={cn(
          "mt-0.5 truncate text-xs",
          row.is_read ? "text-muted-foreground" : "font-medium text-foreground",
        )}>
          {row.subject || "(no subject)"}
        </div>
        <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
          {row.message}
        </div>
      </button>
    </li>
  );
}

function MessageDetail({
  row, onClose, onToggleRead, onDelete, busy,
}: {
  row: ContactMessageRow;
  onClose: () => void;
  onToggleRead: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const replyTo = `mailto:${row.email}?subject=${encodeURIComponent("Re: " + row.subject)}`;
  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-2 border-b border-border/60 px-4 py-3">
        <Button variant="ghost" size="sm" onClick={onClose} className="lg:hidden">
          ← Back
        </Button>
        <Badge variant={row.is_read ? "outline" : "default"} className="font-mono text-[10px]">
          {row.is_read ? "Read" : "Unread"}
        </Badge>
        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onToggleRead} disabled={busy}>
            {row.is_read
              ? <><Mail     className="mr-1.5 h-3.5 w-3.5" />Mark unread</>
              : <><MailOpen className="mr-1.5 h-3.5 w-3.5" />Mark read</>}
          </Button>
          <Button
            variant="ghost" size="sm"
            onClick={onDelete} disabled={busy}
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete
          </Button>
        </div>
      </div>

      {/* Header */}
      <div className="border-b border-border/60 px-5 py-4">
        <h2 className="text-lg font-bold tracking-tight">{row.subject || "(no subject)"}</h2>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
          <span className="font-medium text-foreground">{row.name}</span>
          <a href={`mailto:${row.email}`} className="text-primary hover:underline">
            {row.email}
          </a>
          <span className="ml-auto text-xs text-muted-foreground">
            {formatAbsolute(row.created_at)}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 px-5 py-5">
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
          {row.message}
        </p>
      </div>

      {/* Footer — reply CTA */}
      <div className="border-t border-border/60 px-5 py-3">
        <Button asChild>
          <a href={replyTo}>
            <Mail className="mr-2 h-4 w-4" />Reply via email
          </a>
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tiny date helpers — keep formatting inline so we don't pull in a lib for
// two timestamps. ISO -> "5m" / "2h" / "3d" / "12 Jun".
// ---------------------------------------------------------------------------
function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)    return `${Math.floor(diff)}s`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
function formatAbsolute(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}
